import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "border", "gapColumn", "gapRow", "gridTemplateColumns", "isRefreshing", "minContentInlineSize", "onSort", "overflow", "role", "sortColumn", "sortDirection"],
  _excluded2 = ["as", "background", "children", "position", "zIndex"],
  _excluded3 = ["children", "gap"],
  _excluded4 = ["children", "overflow", "paddingBlock", "paddingInline", "role", "truncate"],
  _excluded5 = ["children", "overflow", "justifyContent", "paddingBlock", "paddingInline", "role"],
  _excluded6 = ["as", "children", "paddingBlock", "paddingInline"],
  _excluded7 = ["children", "fontWeight"];
import styled from 'styled-components';
import { LayoutGrid } from '../Layout/LayoutGrid';
import { LayoutBox } from '../Layout/LayoutBox';
import { LayoutHorizontal } from '../Layout/LayoutHorizontal';
import { LayoutDividerVertical } from '../Layout/LayoutDividerVertical';
import { TruncateString } from '../Typography';
import { GridListRowContainer } from './GridListRowContainer';
import { GridListContext } from './context';
import { GridListHeaderItem, GridListHeaderItemSortable } from './GridListHeaderItem';
import { GridListRefreshProgress } from './GridListRefreshProgress';
import { GridListAction } from './GridListAction';
import { jsx as _jsx } from "react/jsx-runtime";
export const GridList = _ref => {
  let {
      children,
      border = 'border',
      gapColumn = '0',
      gapRow = '0',
      gridTemplateColumns = 'repeat(auto-fit, minmax(5rem, 1fr))',
      isRefreshing = false,
      minContentInlineSize,
      onSort,
      overflow = 'auto',
      role = 'table',
      sortColumn,
      sortDirection
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(GridListContext.Provider, {
    value: {
      gapColumn,
      gapRow,
      gridTemplateColumns,
      isRefreshing,
      onSort,
      sortColumn,
      sortDirection
    },
    children: /*#__PURE__*/_jsx(LayoutGrid, Object.assign({}, rest, {
      border: border,
      gapColumn: gapColumn,
      gapRow: gapRow,
      overflow: overflow,
      role: role,
      children: /*#__PURE__*/_jsx(LayoutDividerVertical, {
        gap: "0",
        minInlineSize: minContentInlineSize,
        position: "relative",
        children: children
      })
    }))
  });
};
const Header = _ref2 => {
  let {
      as = 'header',
      background = 'background-color',
      children,
      position = 'relative',
      zIndex = 100
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  return /*#__PURE__*/_jsx(LayoutBox, Object.assign({}, rest, {
    as: as,
    background: background,
    position: position,
    zIndex: zIndex,
    children: children
  }));
};
GridList.Header = Header;
const HeaderRow = _ref3 => {
  let rest = Object.assign({}, (_objectDestructuringEmpty(_ref3), _ref3));
  return /*#__PURE__*/_jsx(GridListRowContainer, Object.assign({}, rest, {
    tabIndex: -1
  }));
};
GridList.HeaderRow = HeaderRow;
GridList.HeaderItem = GridListHeaderItem;
GridList.HeaderItemSortable = GridListHeaderItemSortable;
const GridListBody = _ref4 => {
  let {
      children,
      gap = '0'
    } = _ref4,
    rest = _objectWithoutPropertiesLoose(_ref4, _excluded3);
  return /*#__PURE__*/_jsx(LayoutDividerVertical, Object.assign({}, rest, {
    gap: gap,
    paddingBlockStart: "space-4xs",
    children: children
  }));
};
GridList.Body = GridListBody;
const Row = _ref5 => {
  let rest = Object.assign({}, (_objectDestructuringEmpty(_ref5), _ref5));
  return /*#__PURE__*/_jsx(GridListRowContainer, Object.assign({}, rest));
};
GridList.Row = Row;
GridList.Action = GridListAction;
const Item = _ref6 => {
  let {
      children,
      overflow = 'hidden',
      paddingBlock = 'space',
      paddingInline = 'space-md',
      role = 'cell',
      truncate = false
    } = _ref6,
    rest = _objectWithoutPropertiesLoose(_ref6, _excluded4);
  return /*#__PURE__*/_jsx(LayoutBox, Object.assign({}, rest, {
    overflow: overflow,
    paddingBlock: paddingBlock,
    paddingInline: paddingInline,
    role: role,
    children: /*#__PURE__*/_jsx(TruncateString, {
      truncate: truncate,
      children: children
    })
  }));
};
GridList.Item = Item;
const ItemAction = _ref7 => {
  let {
      children,
      overflow = 'hidden',
      justifyContent = 'space-between',
      paddingBlock = 'space',
      paddingInline = 'space-md',
      role = 'cell'
    } = _ref7,
    rest = _objectWithoutPropertiesLoose(_ref7, _excluded5);
  return /*#__PURE__*/_jsx(LayoutHorizontal, Object.assign({}, rest, {
    justifyContent: justifyContent,
    overflow: overflow,
    paddingBlock: paddingBlock,
    paddingInline: paddingInline,
    role: role,
    children: children
  }));
};
GridList.ItemAction = ItemAction;
const Footer = _ref8 => {
  let {
      as = 'footer',
      children,
      paddingBlock = '0',
      paddingInline = '0'
    } = _ref8,
    rest = _objectWithoutPropertiesLoose(_ref8, _excluded6);
  return /*#__PURE__*/_jsx(LayoutBox, Object.assign({}, rest, {
    as: as,
    paddingBlock: paddingBlock,
    paddingInline: paddingInline,
    children: children
  }));
};
GridList.Footer = Footer;
const ItemStyled = styled(Item).withConfig({
  displayName: "GridList__ItemStyled",
  componentId: "zckwpg-0"
})(["font-weight:", ";"], ({
  $fontWeight
}) => $fontWeight);
const FooterItem = _ref9 => {
  let {
      children,
      fontWeight = '600'
    } = _ref9,
    rest = _objectWithoutPropertiesLoose(_ref9, _excluded7);
  return /*#__PURE__*/_jsx(ItemStyled, Object.assign({}, rest, {
    $fontWeight: fontWeight,
    children: children
  }));
};
GridList.FooterItem = FooterItem;
GridList.RefreshProgress = GridListRefreshProgress;